import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";

import { CardCategoryEnum } from "@sorcery/db";

import type { DeckCardType } from "~/types/deck";

export const STARTING_SITE_COUNT = 3;
export const STARTING_SPELL_COUNT = 3;

const getDeck = (decklist: DeckCardType[]) => {
  const newDeckSpread = [] as DeckCardType[];
  decklist.forEach((dc) => {
    for (let i = 0; i < dc.quantity; i++) {
      newDeckSpread.push(dc);
    }
  });

  return newDeckSpread;
};

type ISampleHandContext = {
  hand: DeckCardType[];
  drawHand: () => void;
  drawSpell: () => void;
  drawSite: () => void;
  discardCard: (index: number) => void;
};

const SampleHandContext = createContext<ISampleHandContext>({} as ISampleHandContext);

const SampleHandProvider = ({
  decklist,
  children,
}: {
  decklist: DeckCardType[];
  children: ReactNode;
}): React.ReactElement => {
  const [sampleDeck, setSampleDeck] = useState<DeckCardType[]>([]);
  const [hand, setHand] = useState<DeckCardType[]>([]);

  const getSites = (deckCards: DeckCardType[]) => {
    return deckCards.filter((dc) => dc.card.guardian?.category === CardCategoryEnum.Site);
  };

  const getSpells = (deckCards: DeckCardType[]) => {
    return deckCards.filter((dc) => dc.card.guardian?.category === CardCategoryEnum.Spell);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const drawHand = useCallback(() => {
    // set up
    const options = getDeck(decklist);
    const spellOptions = getSpells(options);
    const siteOptions = getSites(options);

    // spells
    const spellsToDraw = [] as DeckCardType[];
    for (let a = 0; a < STARTING_SPELL_COUNT; a++) {
      // find card
      const card = spellOptions[Math.floor(Math.random() * spellOptions.length)];
      // remove from deck
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      spellOptions.splice(spellOptions.indexOf(card!), 1);
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      spellsToDraw.push(card!);
    }

    // sites
    const sitesToDraw = [] as DeckCardType[];
    for (let a = 0; a < STARTING_SITE_COUNT; a++) {
      // find card
      const card = siteOptions[Math.floor(Math.random() * siteOptions.length)];
      // remove from deck
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      siteOptions.splice(siteOptions.indexOf(card!), 1);
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      sitesToDraw.push(card!);
    }

    // save state
    setHand(spellsToDraw.concat(sitesToDraw));
    setSampleDeck(spellOptions.concat(siteOptions));
  }, [decklist]);

  useEffect(() => {
    drawHand();
  }, [drawHand]);

  const drawSpell = () => {
    const spellOptions = getSpells(sampleDeck);
    if (spellOptions.length > 0) {
      // find card
      const card = spellOptions[Math.floor(Math.random() * spellOptions.length)];
      // remove from deck
      const newDeck = sampleDeck.filter((item) => item !== card);
      setSampleDeck(newDeck);
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      setHand(hand.concat([card!]));
    }
  };

  const drawSite = () => {
    const siteOptions = getSites(sampleDeck);
    if (siteOptions.length > 0) {
      // find card
      const card = siteOptions[Math.floor(Math.random() * siteOptions.length)];
      // remove from deck
      const newDeck = sampleDeck.filter((item) => item !== card);
      setSampleDeck(newDeck);
      // biome-ignore lint/style/noNonNullAssertion: <explanation>
      setHand(hand.concat([card!]));
    }
  };

  const discardCard = (index: number) => {
    const newHand = hand.filter((_, i) => i !== index);
    setHand(newHand);
  };

  return (
    <SampleHandContext.Provider
      value={{
        hand,
        drawHand,
        drawSpell,
        drawSite,
        discardCard,
      }}
    >
      {children}
    </SampleHandContext.Provider>
  );
};

const useSampleHand = (): ISampleHandContext => useContext(SampleHandContext);
export { SampleHandProvider, useSampleHand };
